import { useContext, useEffect, useState } from "react";
import { LocaleContext } from "../../contexts/LocaleContext";
import { useNavigate, useParams } from "react-router-dom";
import { useValidation } from "../../hooks/useValidation";
import { createProviderSchema } from "../../validation/providerSchema";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "../../services/toastService";
import {providerService} from "../../services/providerService";
import { cityService } from "../../services/cityService";
import { bankService } from "../../services/bankService";
import Loading from "../../components/common/Loading";
import BackButton from "../../components/common/BackButton";
import { urlConfig } from "../../config/baseUrl";

const ProviderForm = () => {
  const { t, lang } = useContext(LocaleContext);
  const { id } = useParams();
  const isEditMode=Boolean(id);
  const navigate = useNavigate();
  const [btnLoading, setBtnLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [banks, setBanks] = useState([]);
  const [isFilePicked, setIsFilePicked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const initialForm = {
    fullName: "",
    phoneNumberCode: "+966",
    phoneNumber: "",
    cityId: "",
    bankId: "",
    gender: "",
    age: "",
    iban: "",
    email: "",
    about: "",
    file: null,
    balance: 0,
  };

  useEffect(() => {
      loadData();
    }, []);
    const loadData = async () => {
      setLoading(true);
      await getAllCities();
      await getAllBanks();
      if (isEditMode) await editAsync();
      setLoading(false);
    };
  const getAllCities = async () => {
    var data = await cityService.getDropdownAsync();
    setCities(data);
  }
  const getAllBanks = async () => {
    var data = await bankService.getDropdownAsync();
    setBanks(data);
  }
  const handleClear = () => {
    reset(initialForm); // resets to initial values
  };
  const v = useValidation();
  const schema = createProviderSchema(v, t, isEditMode);
   //validation
    const {
      register,
      handleSubmit,
      setValue,
      control,
      reset,
      formState: { errors },
      watch,
    } = useForm({
      defaultValues: initialForm,
      resolver: yupResolver(schema),
    });
const onSubmit = async (values) => {
  try {
    const formData = new FormData();
    // Append DTO fields
    Object.entries(values).forEach(([key, value]) => {
      if (value !== null && value !== undefined && key !== "file") {
        formData.append(key, value);
      }
    });

    // Append file (must match backend parameter name)
    if (values.file) {
      formData.append("file", values.file);
    }
    if (isEditMode) {
     await updateAsync(formData);
    } else {
      await addAsync(formData);
    }
  } catch (error) {
    toast.error(t("anErrorOccurredPleaseTryAgainLater"));
    console.log(error);
  } 
};
const addAsync = async (formData) => {
  try {
    setBtnLoading(true);
    var response = await providerService.create(formData);
    if (!response.isSuccess) {
      response.code===2?toast.error(t("thisPhoneNumberAlreadyExists")): toast.error(t("anErrorOccurredPleaseTryAgainLater"));
      console.log(response);
      return;
    }
    toast.success(t("recordAddedSuccessfully"));
    navigate("/providers");
  } catch (error) {
    toast.error(t("anErrorOccurredPleaseTryAgainLater"));
    console.log(error);
  } finally {
    setBtnLoading(false);
  }
};
 const editAsync = async () => {
    const response = await providerService.edit(id);

    if (!response.isSuccess) {
      toast.error(t("anErrorOccurredPleaseTryAgainLater"));
      return;
    }
    const data = response.value;
    console.log(data);
    setPreviewImage(data.imagePath);
    reset({
      fullName: data.fullName,
      phoneNumberCode: data.phoneNumberCode,
      phoneNumber: data.phoneNumber,
      cityId: data.cityId,
      bankId: data.bankId,
      gender: data.gender,
      age: data.age,
      iban: data.iban,
      email: data.email,
      about: data.about,
      balance: data.balance,
      file: null, // cannot set file input from API
    });
  };
const updateAsync = async (formData) => {
  try {
    setBtnLoading(true);
    var response = await providerService.update(id, formData);
    if (!response.isSuccess) {
       response.code === 2
         ? toast.error(t("thisPhoneNumberAlreadyExistsForAnotherUser"))
         : toast.error(t("anErrorOccurredPleaseTryAgainLater"));
      console.log(response);
      return;
    }
    toast.success(t("recordUpdatedSuccessfully"));
    navigate("/providers");
  } catch (error) {
    toast.error(t("anErrorOccurredPleaseTryAgainLater"));
    console.log(error);
  } finally {
    setBtnLoading(false);
  }
};
const deleteImage = async () => {
    try {
      setLoading(true);
      var response = await providerService.deleteImageAsync(id);
      if (!response.isSuccess) {
        toast.error(t("anErrorOccurredPleaseTryAgainLater"));
        return;
      } else {
        toast.success(t("recordDeletedSuccessfully"));
        setPreviewImage(null);
      }
    } catch (error) {
      toast.error(t("anErrorOccurredPleaseTryAgainLater"));
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
 
if(loading) return <Loading />;
  return (
    <section className="provider-form">
      <div className="default-heading">
        {t("providersDashboardAndManagement")}
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="my-card">
          <div className="my-card-header d-flex justify-content-between align-items-center">
            {isEditMode ? (
              <>
                <i className="fas fa-edit"></i> {t("editOrSelectedNarrator")}
              </>
            ) : (
              <>
                <i className="fas fa-plus plus-icon"></i> {t("addNewProvider")}
              </>
            )}
            <BackButton />
          </div>
          <div className="my-card">
            <div className="my-card-header text-primary">
              {t("basicInformation")}
            </div>
            <div className="row">
              <div className="col-12">
                <div className="form-group">
                  <label className="form-label">
                    {t("fullName")} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    {...register("fullName")}
                    className="form-control"
                    placeholder={t("fullName")}
                  />
                  {errors.fullName && (
                    <p className="text-danger">{errors.fullName.message}</p>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label className="form-label">
                    {t("phoneNumberCode")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    {...register("phoneNumberCode")}
                    disabled
                  >
                    <option value="+966">+966</option>
                  </select>
                  {errors.phoneNumberCode && (
                    <p className="text-danger">
                      {errors.phoneNumberCode.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label className="form-label">
                    {t("phoneNumber")} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder={t("phoneNumber")}
                    {...register("phoneNumber")}
                  />
                  {errors.phoneNumber && (
                    <p className="text-danger">{errors.phoneNumber.message}</p>
                  )}
                </div>
              </div>
              <div className="col-12">
                <div className="form-group">
                  <label className="form-label">{t("email")}</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("email")}
                    {...register("email")}
                  />
                  {errors.email && (
                    <p className="text-danger">{errors.email.message}</p>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label className="form-label">
                    {t("gender")} <span className="text-danger">*</span>
                  </label>
                  <select className="form-control" {...register("gender")}>
                    <option value="">{t("select")}</option>
                    <option value="1">{t("male")}</option>
                    <option value="2">{t("female")}</option>
                  </select>
                  {errors.gender && (
                    <p className="text-danger">{errors.gender.message}</p>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label className="form-label">
                    {t("age")} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder={t("age")}
                    {...register("age")}
                  />
                  {errors.age && (
                    <p className="text-danger">{errors.age.message}</p>
                  )}
                </div>
              </div>
              <div className="col-12">
                <div className="form-group">
                  <label className="form-label">{t("about")}</label>
                  <textarea
                    className="form-control"
                    placeholder={t("about")}
                    {...register("about")}
                  />
                  {errors.about && (
                    <p className="text-danger">{errors.about.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="my-card mt-3">
            <div className="my-card-header text-primary">
              {t("accountInformation")}
            </div>
            <div className="row">
              <div className="col-12">
                <div className="form-group">
                  <label className="form-label">
                    {t("city")} <span className="text-danger">*</span>
                  </label>
                  <select className="form-control" {...register("cityId")}>
                    <option value="">{t("selectCity")}</option>
                    {cities?.map((city) => (
                      <option key={city.id} value={city.id}>
                        {city.name[lang]}
                      </option>
                    ))}
                  </select>
                  {errors.cityId && (
                    <p className="text-danger">{errors.cityId.message}</p>
                  )}
                </div>
              </div>
              {!previewImage ? (
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label">{t("profilePicture")}</label>

                    <input
                      type="file"
                      id="provider-profile-picture"
                      className="d-none"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];

                        if (file) {
                          setValue("file", file);
                          setIsFilePicked(true);
                        }
                      }}
                    />

                    <label
                      htmlFor="provider-profile-picture"
                      className="my-file-upload"
                    >
                      <span>
                        {t("dragAndDropTheImageHereOr")}{" "}
                        <span className="text-primary">
                          {t("browseImages")} {isFilePicked && "(1)"}
                        </span>
                      </span>

                      <i className="fas fa-camera" />
                    </label>
                  </div>
                </div>
              ) : (
                <div className="col-12">
                  <div className="form-group">
                    <div className="preview-images">
                      <div className="preview-image">
                        <img
                          src={`${urlConfig.imageBaseUrl}/${previewImage}`}
                        />
                        <i
                          className="fas fa-times"
                          onClick={() => deleteImage()}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label className="form-label">{t("ibanNo")}</label>
                  <input
                    type="text"
                    {...register("iban")}
                    className="form-control"
                    placeholder={t("ibanNo")}
                  />
                  {errors.iban && (
                    <p className="text-danger">{errors.iban.message}</p>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label className="form-label">{t("balance")} <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    {...register("balance")}
                    className="form-control"
                    placeholder={t("balance")}
                  />
                  {errors.balance && (
                    <p className="text-danger">{errors.balance.message}</p>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label className="form-label">{t("bankName")}</label>
                  <select className="form-control" {...register("bankId")}>
                    <option value="">{t("select")}</option>
                    {banks?.map((bank) => (
                      <option key={bank.id} value={bank.id}>
                        {bank.name[lang]}
                      </option>
                    ))}
                  </select>
                  {errors.bankId && (
                    <p className="text-danger">{errors.bankId.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 gap-2 d-flex justify-content-end">
            {!btnLoading && (
              <button type="submit" className="btn-default">
                {t("save")}
              </button>
            )}
            {btnLoading && (
              <button type="button" className="btn-default">
                <span
                  class="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              </button>
            )}
            {isEditMode && (
              <button
                type="button"
                className="btn-secondary"
                onClick={handleClear}
              >
                {t("reset")}
              </button>
            )}
            {!isEditMode && (
              <button
                type="button"
                className="btn-secondary"
                onClick={handleClear}
              >
                {t("reset")}
              </button>
            )}
          </div>
        </div>
      </form>
    </section>
  );
};

export default ProviderForm;
